import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Phone, Search, Scale, Clock, ShieldAlert, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { isValidRut, formatRut } from "@/lib/rut";
import { track } from "@/lib/analytics";
import ConsentCheckbox, { ScanConsentText } from "@/components/ConsentCheckbox";
import ScanLoading from "@/components/ScanLoading";
import SEO, { SITE_ORIGIN } from "@/components/SEO";
import { toast } from "sonner";

const BROKERS = [
  "findatos.com", "nombrerutyfirma.com", "rutificador.cl",
  "infopersonas.cl", "rutynombre.com", "elrutificador.cl", "rutificadorcl.cl",
  "buscarut.com", "rutchile.cl",
  "contakta.cl", "thefast.cl", "baseschile.com", "basesempresaschile.cl",
  "basesdedatoschile.cl", "corparesearch.com", "infodatoschile.com",
];

const FAQS = [
  { q: "¿Qué es borra-me.com?",
    a: "borra-me.com es una plataforma chilena que automatiza dos cosas: (1) eliminar tus datos personales de los principales rutificadores y brokers de datos de Chile, y (2) detener el spam de bancos, telcos y retail mediante solicitudes formales de oposición al marketing, todo usando los derechos ARCOP consagrados en la Ley 21.719." },
  { q: "¿Cuánto cuesta el servicio?",
    a: "$39.990 CLP como pago único (Webpay Transbank), sin permanencia ni renovaciones. Incluye escaneo de los principales brokers chilenos, solicitudes ARCOP formales con base legal por responsable, gestión de empresas (bancos, telcos, retail) para detener el spam, seguimiento automatizado, escalada a la APDP si no responden en 30 días, y re-escaneo cada 45 días de forma vitalicia. Además obtienes 5 escaneos gratuitos adicionales para otros RUT (familia o socios). Para comparar: un abogado cobra entre $150.000 y $300.000 por gestionar una sola solicitud ante un solo responsable." },
  { q: "¿Es legal lo que hace borra-me.com?",
    a: "Completamente legal. Ejercemos los derechos que la Ley 21.719 le otorga a cada titular de datos en Chile: Acceso, Rectificación, Cancelación, Oposición y Portabilidad (ARCOP). Actuamos con el consentimiento explícito del usuario y con base legal específica en cada solicitud." },
  { q: "¿Cuánto tiempo toma eliminar mis datos?",
    a: "Los responsables (brokers y empresas) tienen 30 días corridos para responder según el artículo 15 de la Ley 21.719. La mayoría de las eliminaciones se confirman entre 5 y 15 días. Si un responsable no responde en 30 días, generamos automáticamente el borrador de denuncia ante la APDP." },
  { q: "¿Qué pasa si mis datos vuelven a aparecer?",
    a: "Re-escaneamos todos los brokers cada 45 días. Si tus datos reaparecen en algún sitio, abrimos automáticamente una nueva solicitud ARCOP sin costo adicional. La suscripción es vigilancia continua." },
  { q: "¿Necesito saber de leyes para usar borra-me.com?",
    a: "No. Tú ingresas tu nombre y RUT. Nosotros generamos las solicitudes con la base legal correcta, hacemos el seguimiento y escalamos si es necesario. El proceso legal completo está automatizado." },
  { q: "¿Qué es la Ley 21.719?",
    a: "Es la nueva Ley de Protección y Tratamiento de Datos Personales de Chile, publicada el 13 de diciembre de 2024 y en plena vigencia desde el 1 de diciembre de 2026. Crea la Agencia de Protección de Datos Personales (APDP), consagra los derechos ARCOP en el art. 12, fija un plazo de 30 días corridos para responder en el art. 15, y establece multas de hasta $1.397 millones CLP en el art. 49." },
  { q: "¿Qué son los rutificadores y por qué tienen mis datos?",
    a: "Son sitios web que recopilan datos personales de fuentes públicas, filtraciones y bases compradas. Monetizan vendiéndolos a call centers y, en algunos casos, a actores maliciosos. En Chile mantenemos escáner activo sobre los principales activos del mercado (findatos, rutificador.cl, elrutificador.cl, buscarut, rutchile, contakta, thefast, etc.). Tu RUT, nombre, dirección y teléfono probablemente están en varios aunque nunca te hayas registrado." },
  { q: "¿Qué pasa con mis propios datos que le entrego a borra-me.com?",
    a: "Usamos tu nombre y RUT únicamente para ejecutar el escaneo y las solicitudes ARCOP. No vendemos, compartimos ni cedemos tu información a terceros. Operamos bajo los mismos principios de la Ley 21.719 que exigimos a los brokers." },
  { q: "¿Puedo cancelar mi suscripción?",
    a: "Sí, en cualquier momento desde tu dashboard. Sin períodos mínimos. Sin penalizaciones. Las solicitudes ya enviadas siguen activas hasta que los brokers confirmen la eliminación." },
];

export default function Home() {
  const [nombre, setNombre] = useState("");
  const [rut, setRut] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  const onScan = async (e) => {
    e.preventDefault();
    if (!nombre.trim() || !rut.trim()) { toast.error("Ingresa nombre y RUT"); return; }
    if (!isValidRut(rut)) { toast.error("RUT inválido. Verifica el dígito verificador."); return; }
    if (!consent) { toast.error("Debes autorizar el tratamiento de tus datos para escanear (Ley 21.719)"); return; }
    track("scan_started", { source: "home_hero" });
    setLoading(true);
    try {
      const { data } = await api.post("/scan/free", { nombre, rut: formatRut(rut) }, { timeout: 45000 });
      sessionStorage.setItem("lastScan", JSON.stringify(data));
      const exposed = data?.totals?.exposed ?? data?.with_data_count ?? 0;
      track("scan_completed", {
        scan_id: data.scan_id,
        teaser: !!data.teaser,
        score: data.score,
        exposed_count: exposed,
      });
      navigate(`/scan/${data.scan_id}`);
    } catch (err) {
      if (err.response?.status === 429) {
        track("scan_rate_limited", { from: "home" });
        toast.error("Llegaste al límite gratuito (2 escaneos). Crea tu cuenta para continuar.");
        navigate("/register?from=limit");
        return;
      }
      track("scan_error", { status: err.response?.status || 0 });
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo escanear");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#F9F9F8]">
      <SEO
        title="borra-me.com — Elimina tu RUT de rutificadores y detén el spam (Ley 21.719)"
        description="Escaneamos los principales brokers chilenos que exponen tu RUT y enviamos solicitudes ARCOP formales (Ley 21.719). Detenemos el spam de bancos, telcos y retail. Escalada automática a la APDP si no responden en 30 días. Pago único $39.990."
        canonical="/"
        keywords="eliminar datos personales Chile, rutificador eliminar, sacar RUT de internet, Ley 21.719 ARCOP, detener spam banco Chile, no molestar sernac"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "borra-me.com — Eliminación de datos personales y anti-spam",
            provider: { "@type": "Organization", name: "borra-me.com", url: SITE_ORIGIN },
            areaServed: { "@type": "Country", name: "Chile" },
            serviceType: "Data Broker Removal / ARCOP enforcement",
            description: "Escaneo automatizado de brokers chilenos, solicitudes ARCOP y de oposición al marketing bajo Ley 21.719, escalada APDP y vigilancia continua.",
            offers: {
              "@type": "Offer",
              price: "39990",
              priceCurrency: "CLP",
              priceValidUntil: "2027-12-31",
              eligibleRegion: "CL",
              category: "Pago único",
              url: `${SITE_ORIGIN}/`,
              availability: "https://schema.org/InStock",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map(f => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-gray-200">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-blue-50 blur-3xl opacity-50" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7">
            <div className="eyebrow mb-5" data-testid="hero-eyebrow">
              Chile · 9.714 millones de llamadas spam en 2025 · Tu número está en la lista
            </div>
            <h1 data-testid="hero-h1" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.05]">
              Tu RUT en internet.<br />
              Tu bandeja llena de spam.<br />
              <span className="text-[#DC2626]">Limpieza completa de huella.</span>
            </h1>
            <p className="mt-7 text-lg text-gray-700 max-w-2xl leading-relaxed">
              Escaneamos los <b>brokers</b> que exponen tu RUT y enviamos solicitudes ARCOP de <b>supresión total</b>.
              Detenemos el <b>spam de bancos, telcos y retail</b> con solicitudes de <b>oposición al marketing</b>.
              Si no cumplen en 30 días, los denunciamos ante la APDP. <b>Tú no haces nada.</b>
            </p>
          </motion.div>

          {/* Scan form card */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="lg:col-span-5">
            <form onSubmit={onScan} className="bg-white border border-gray-200 p-7 shadow-[0_4px_0_0_rgba(15,23,42,0.04)]" data-testid="scan-form">
              <div className="eyebrow mb-3">Escáner gratis · sin tarjeta · sin registro</div>
              <label className="block text-xs font-bold tracking-wider uppercase text-gray-500 mt-4 mb-2">Nombre completo</label>
              <input data-testid="hero-name-input" value={nombre} onChange={e => setNombre(e.target.value)}
                placeholder="Ej: Felipe Soto Martínez" className="w-full border border-gray-300 px-4 py-3 text-gray-900 focus:border-[#0B2447] focus:outline-none" />
              <label className="block text-xs font-bold tracking-wider uppercase text-gray-500 mt-4 mb-2">RUT</label>
              <input data-testid="hero-rut-input" value={rut} onChange={e => setRut(e.target.value)}
                placeholder="12.345.678-9" className="w-full border border-gray-300 px-4 py-3 text-gray-900 focus:border-[#0B2447] focus:outline-none" />
              <div className="mt-5">
                <ConsentCheckbox
                  checked={consent}
                  onChange={setConsent}
                  testid="hero-scan-consent"
                >
                  <ScanConsentText />
                </ConsentCheckbox>
              </div>
              <button type="submit" disabled={loading || !consent} data-testid="hero-scan-btn"
                className="mt-5 w-full bg-[#0F172A] hover:bg-black text-white px-6 py-4 font-semibold flex items-center justify-center gap-2 disabled:opacity-80 disabled:cursor-not-allowed">
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Analizando y buscando tus datos…
                  </>
                ) : (
                  <>Ver mis datos expuestos <ArrowRight className="w-5 h-5" /></>
                )}
              </button>
              <p className="text-xs text-gray-500 text-center mt-3">Sin tarjeta · sin registro · solo nombre + RUT</p>
            </form>
            <div className="mt-5 bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-900 leading-relaxed">
              <b>⚠️ La Ley 21.719 entra en vigencia el 1 de diciembre de 2026.</b> Los brokers que no cumplan arriesgan multas de hasta $1.397 millones CLP. Tu derecho existe. Ejercerlo solo toma 2 minutos.
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-[#0F172A] text-white" data-testid="stats-bar">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <Stat n="9.714M" label="llamadas spam recibidas en Chile en 2025" />
          <Stat n="41.703" label="causas de fraude solo en Q3 2025" />
          <Stat n="14" label="sitios que exponen tu RUT hoy mismo" />
          <Stat n="30 días" label="plazo legal para que te eliminen" />
        </div>
      </section>

      {/* PROBLEMA */}
      <section className="border-b border-gray-200 bg-[#F9F9F8]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Escribiste tu nombre en Google.<br />
            <span className="text-gray-500">Lo que apareció no debería estar ahí.</span>
          </h2>
          <div className="mt-10 grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3 space-y-5 text-gray-700 text-[15px] leading-relaxed">
              <p>En Chile existen al menos <b>16 sitios web que venden tu información personal sin tu permiso</b>.</p>
              <p>Tu nombre completo. Tu RUT. Tu dirección. Tu teléfono. Tu email. En algunos casos, tu profesión, tu renta estimada y con quién vives.</p>
              <p>No lo pusiste tú. No lo autorizaste. Simplemente está ahí, disponible para cualquier persona con acceso a internet.</p>
              <p className="font-semibold text-gray-900 pt-2">¿Qué hacen con esa información?</p>
              <ul className="space-y-2 pl-5 list-disc marker:text-[#DC2626]">
                <li>Call centers compran bases de datos para llamarte 31 veces al mes</li>
                <li>Estafadores construyen perfiles para suplantarte</li>
                <li>Ex parejas violentas encuentran tu dirección actual</li>
                <li>Robo de identidad: 41.703 causas de fraude en Chile solo en el tercer trimestre de 2025</li>
                <li>Phishing personalizado: 6,3 millones de intentos bloqueados entre enero y octubre de 2025</li>
              </ul>
              <p className="pt-2 border-l-4 border-[#DC2626] pl-4 italic">
                El spam que recibes todos los días no es aleatorio.<br />
                Alguien pagó por tu número. Ese alguien lo compró de uno de esos 16 sitios.
              </p>
            </div>
            <div className="lg:col-span-2 space-y-4">
              <StatCard icon={Phone} stat="31 llamadas spam" caption="al mes por chileno en 2025. Chile: 2° país con más spam en LatAm." />
              <StatCard icon={Search} stat="5 segundos" caption="es lo que tarda alguien en encontrar tu dirección con tu nombre y RUT." />
              <StatCard icon={Scale} stat="$1.397 millones CLP" caption="multa máxima para brokers que incumplan la Ley 21.719 desde diciembre 2026." />
              <StatCard icon={Clock} stat="70 horas" caption="tomaría eliminarte de los 16 sitios manualmente. Nosotros lo hacemos en días." />
            </div>
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 leading-tight max-w-4xl">
            Tú ingresas tu nombre.<br />
            <span className="text-gray-500">Nosotros hacemos el resto.</span>
          </h2>
          <div className="mt-14 grid lg:grid-cols-4 gap-8">
            <Step n="01" tag="Gratis · Sin registro · 15s" title="Escaneamos los 16 sitios en tiempo real"
              body="Ingresa tu nombre y RUT. Nuestro sistema revisa automáticamente los 16 principales brokers de datos en Chile: findatos.com, nombrerutyfirma.com, rutificador.cl, buscarut.com, rutchile.cl y 11 más. En 15 segundos ves exactamente en cuántos sitios apareces — y qué campos están expuestos." />
            <Step n="02" tag="Al suscribirte" title="Generamos solicitudes ARCOP con base legal exacta"
              body="Cada solicitud cita el artículo específico de la Ley 21.719 que aplica a ese responsable. No es un email genérico. Es una notificación legal formal enviada desde notificacion@borra-me.com con un link único y trazable. 30 días corridos para responder o enfrentan a la APDP." />
            <Step n="03" tag="Automático" title="Hacemos el seguimiento por ti"
              body="Día 15 sin respuesta → email de escalada automático al responsable. Día 30 sin respuesta → generamos el borrador de denuncia ante la APDP. Tú recibes actualizaciones en tu dashboard: respondió, eliminó, rechazó, sin respuesta." />
            <Step n="04" tag="Cada 45 días" title="Re-escaneamos para que no vuelvan a aparecer"
              body="Algunos brokers reindexan datos desde otras fuentes. Si tus datos reaparecen, abrimos una nueva solicitud automáticamente. La suscripción no es un pago por acción única. Es vigilancia continua de tu privacidad digital." />
          </div>
        </div>
      </section>

      {/* DATOS EXPUESTOS */}
      <section className="border-b border-gray-200 bg-[#F9F9F8]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Esto es lo que aparece cuando alguien busca tu RUT
          </h2>
          <p className="text-gray-600 text-lg mt-4">No es hipotético. Es lo que encontramos en el <b className="text-gray-900">94% de los escaneos</b>.</p>
          <div className="mt-10 grid sm:grid-cols-2 gap-x-12 gap-y-3 text-gray-800 text-[15px]">
            {[
              "Nombre completo", "RUT con dígito verificador", "Dirección actual o anterior",
              "Comuna y región", "Teléfono fijo y/o móvil", "Correo electrónico",
              "Sexo y edad", "Profesión declarada", "Renta estimada",
              "Con quién vives (en algunos brokers Tier 2)",
            ].map(d => (
              <div key={d} className="flex items-start gap-3 border-b border-gray-200 py-3">
                <span className="text-[#DC2626] font-bold">✓</span> {d}
              </div>
            ))}
          </div>
          <p className="mt-10 text-gray-700 text-[15px] leading-relaxed max-w-2xl">
            Esta información <b>no la vendiste tú</b>. La recopilaron sin tu conocimiento de registros públicos,
            filtraciones de datos y bases compradas ilegalmente. La <b>Ley 21.719</b> te da el derecho de eliminarla.
            borra-me.com ejerce ese derecho por ti.
          </p>
        </div>
      </section>

      {/* PRECIO */}
      <section id="precio" className="border-b border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Sin abogados. Sin formularios. Sin seguimiento manual.<br />
            <span className="text-[#DC2626]">$39.990 pago único y nosotros hacemos todo.</span>
          </h2>
          <p className="text-gray-600 text-lg mt-4">Sin contratos de largo plazo. Cancela cuando quieras.</p>

          <div className="mt-12 grid lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-3 bg-[#0F172A] text-white p-10" data-testid="pricing-card">
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-400">borra-me.com · pago único</div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-display text-6xl font-bold tracking-tight">$39.990</span>
                <span className="text-gray-400">CLP · una sola vez</span>
              </div>
              <ul className="mt-8 space-y-3 text-sm text-gray-100">
                {[
                  "Escaneo real de los principales brokers y rutificadores de Chile",
                  "Detener spam de bancos, telcos y retail (Empresas)",
                  "Solicitudes ARCOP formales con base legal específica por responsable",
                  "Seguimiento automatizado con estados en tiempo real",
                  "Escalada automática a APDP si no responden en 30 días (art. 15 Ley 21.719)",
                  "Re-escaneo automático cada 45 días — vigilancia vitalicia sobre tu RUT",
                  "Dashboard con timeline completo por broker y por empresa",
                  "Generación de borrador de denuncia formal incluida",
                  "5 escaneos gratuitos adicionales para otros RUT (familia, socios)",
                ].map(f => (
                  <li key={f} className="flex items-start gap-3">
                    <span className="text-[#DC2626] flex-shrink-0 mt-1">✓</span>
                    <span className="leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate("/register")} data-testid="pricing-cta"
                className="mt-9 w-full bg-white text-[#0F172A] hover:bg-gray-100 px-6 py-4 font-semibold flex items-center justify-center gap-2">
                Comenzar ahora <ArrowRight className="w-5 h-5" />
              </button>
              <p className="text-xs text-gray-400 text-center mt-3">Pago único · Sin renovaciones · Webpay Transbank</p>
            </div>

            <div className="lg:col-span-2 bg-amber-50 border border-amber-200 p-8 text-sm text-gray-800 leading-relaxed">
              <div className="eyebrow mb-3 text-amber-900">¿Por qué $39.990?</div>
              <p>Un abogado en Chile cobra entre <b>$150.000 y $300.000</b> por gestionar una sola solicitud ARCOP ante <b>un solo responsable</b>.</p>
              <p className="mt-3">Tú tienes datos en <b>múltiples brokers</b> y probablemente recibes spam de <b>10+ empresas</b> (banco, telco, retail…).</p>
              <p className="mt-3 font-mono text-sm bg-white border border-amber-300 px-3 py-2">20 × $150.000 = <b>$3.000.000</b> en honorarios legales.</p>
              <p className="mt-3">Una sola vez. Sin seguimiento, sin re-escaneos, sin escalada.</p>
              <p className="mt-4 pt-4 border-t border-amber-300">
                Con borra-me.com pagas <b>$39.990 una sola vez</b> y tienes vigilancia vitalicia sobre tu RUT — brokers, empresas, seguimiento, escalada legal y re-escaneos cada 45 días incluidos.
              </p>
              <p className="mt-4 font-semibold text-[#DC2626]">No es un servicio barato. Es el precio justo por lo que realmente vale.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PARA QUIÉN */}
      <section className="border-b border-gray-200 bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 leading-tight max-w-4xl">
            Si alguna de estas situaciones te describe,<br />
            <span className="text-gray-500">tus datos están en riesgo hoy.</span>
          </h2>
          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            <Persona icon="📱" title="Recibes más de 10 llamadas spam al día"
              body="Felipe trabaja en Las Condes. Recibe llamadas de “bancos”, “AFP” y “seguros” que él nunca contrató. Todas las semanas. Su número está en findatos.com, nombrerutyfirma.com y 6 brokers más. Pagó $39.990 una sola vez. En 3 días las solicitudes ARCOP ya estaban enviadas a los 11 sitios donde aparecía." />
            <Persona icon="🔒" title="Necesitas que tu dirección desaparezca"
              body="Catalina se separó hace 8 meses. Su dirección actual sigue apareciendo en nombrerutyfirma.com y rutificador.cl. Cualquiera que busque su RUT puede llegar a su puerta. Esto no es hipotético. Es el uso más urgente que tiene borra-me.com." />
            <Persona icon="🏢" title="Tu RUT personal está mezclado con el de tu empresa"
              body="Rodrigo es contador y tiene empresa propia. Su RUT aparece en 11 sitios con su dirección de oficina, teléfono celular y renta estimada. Cualquier cliente, competidor o estafador puede ver exactamente cuánto gana. $39.990 una sola vez es menos que lo que cobra un abogado por la primera consulta." />
          </div>
        </div>
      </section>

      {/* URGENCIA LEY */}
      <section className="bg-[#0F172A] text-white border-b border-gray-800">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="flex items-center gap-3 mb-6">
            <ShieldAlert className="w-6 h-6 text-[#DC2626]" />
            <div className="eyebrow text-gray-400">Urgencia regulatoria</div>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
            El 1 de diciembre de 2026 cambia todo.
          </h2>
          <div className="mt-8 space-y-5 text-gray-200 text-[15px] leading-relaxed max-w-3xl">
            <p>La Ley 21.719 entra en vigencia en diciembre de 2026. Por primera vez en Chile, los brokers de datos tienen una <b className="text-white">fecha límite con consecuencias reales</b>.</p>
            <p>La Agencia de Protección de Datos Personales (APDP) comenzará a fiscalizar y multar. El Registro Nacional de Sanciones será público. Los brokers que ignoraban tus solicitudes van a tener que responder.</p>
            <p>Pero tus solicitudes tienen que estar enviadas y documentadas <b className="text-white">antes de esa fecha</b> para que tengan el máximo peso legal.</p>
            <p className="text-[#DC2626] font-semibold pt-2">Cada día que pasa, más personas acceden a tu información.</p>
          </div>
          <button onClick={() => document.querySelector('[data-testid="hero-name-input"]')?.scrollIntoView({behavior:"smooth", block:"center"})}
            data-testid="urgency-cta"
            className="mt-10 bg-[#DC2626] hover:bg-red-700 text-white px-7 py-4 font-semibold inline-flex items-center gap-2">
            Escanear mis datos ahora — es gratis <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* PRIMERAS 24H */}
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Lo que pasa en las primeras 24 horas<br />
            <span className="text-gray-500">después de activar tu cuenta</span>
          </h2>
          <div className="mt-12 space-y-6">
            {[
              { t: "Minuto 1", body: "Escaneamos los principales brokers chilenos en paralelo" },
              { t: "Hora 2", body: "Enviamos solicitudes ARCOP a cada broker con datos y a cada empresa que marcaste (oposición al marketing)" },
              { t: "Día 15", body: "Primer seguimiento automático si no hay respuesta" },
              { t: "Día 30", body: "Escalada a la APDP con PDF de denuncia generado automáticamente" },
              { t: "Día 45", body: "Re-escaneo automático — vigilancia activa permanente" },
            ].map(s => (
              <div key={s.t} className="flex items-baseline gap-6 border-l-2 border-[#DC2626] pl-6">
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#DC2626] w-20 flex-shrink-0">{s.t}</div>
                <div className="text-gray-800 text-[15px]">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-b border-gray-200 bg-[#F9F9F8]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Preguntas frecuentes
          </h2>
          <div className="mt-10 divide-y divide-gray-200 border-y border-gray-200">
            {FAQS.map((f, i) => (
              <button key={i} onClick={() => setOpenFaq(openFaq === i ? null : i)}
                data-testid={`faq-${i}`}
                className="w-full text-left py-5 flex items-start gap-4 hover:bg-white px-2 transition-colors">
                <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 mt-1 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">{f.q}</div>
                  {openFaq === i && <div className="mt-3 text-gray-700 text-[15px] leading-relaxed">{f.a}</div>}
                </div>
              </button>
            ))}
          </div>
        </div>
        {/* NOTE: FAQPage JSON-LD is emitted via <SEO/> at the top of the page. */}
      </section>

      {/* BROKERS strip */}
      <section className="bg-[#F9F9F8] pt-2 pb-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="eyebrow text-gray-500 mb-4">Cobertura · Brokers activos en Chile</div>
          <div className="flex flex-wrap gap-2 font-mono text-xs">
            {BROKERS.map(b => (
              <span key={b} className="border border-gray-300 px-3 py-1.5 text-gray-600 bg-white">{b}</span>
            ))}
          </div>
        </div>
      </section>
      <ScanLoading open={loading} />
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div data-testid="stat">
      <div className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-none">{n}</div>
      <div className="mt-3 text-xs text-gray-400 uppercase tracking-wider leading-relaxed">{label}</div>
    </div>
  );
}

function StatCard({ icon: Icon, stat, caption }) {
  return (
    <div className="bg-white border border-gray-200 p-5">
      <Icon className="w-5 h-5 text-[#DC2626] mb-3" strokeWidth={1.5} />
      <div className="font-display text-lg font-bold text-gray-900">{stat}</div>
      <div className="text-xs text-gray-600 mt-1 leading-relaxed">{caption}</div>
    </div>
  );
}

function Step({ n, tag, title, body }) {
  return (
    <div className="border-l-2 border-[#0F172A] pl-5">
      <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#DC2626] mb-2">{n} · {tag}</div>
      <div className="font-display text-lg font-bold text-gray-900 leading-tight">{title}</div>
      <p className="text-sm text-gray-700 mt-3 leading-relaxed">{body}</p>
    </div>
  );
}

function Persona({ icon, title, body }) {
  return (
    <div className="bg-white border border-gray-200 p-7">
      <div className="text-3xl mb-4">{icon}</div>
      <div className="font-display text-lg font-bold text-gray-900 leading-tight">{title}</div>
      <p className="text-sm text-gray-700 mt-4 leading-relaxed">{body}</p>
    </div>
  );
}
